import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';

import { routeAnimations } from '../../../animations';
import { Customer, SiteState } from '../../../shared/models/shared.model';
import { setSelectedCustomerId, clearSelectedCustomer } from '../../store/customer.actions';
import { getSelectedCustomer } from '../../store/customer.selectors';

@Component({
  selector: 'app-customer-item',
  templateUrl: './customer-item.component.html',
  animations: [routeAnimations],
  standalone: false,
})
export class CustomerItemComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly routeAnimationState = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.route.firstChild?.routeConfig?.data ?? false),
    ),
    { initialValue: this.route.firstChild?.routeConfig?.data ?? false },
  );

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getUrl));

  pageTitle$: Observable<string> = this.store.pipe(
    select(getSelectedCustomer),
    withLatestFrom(this.routeUrl$),
    map(([customer, url]: [Customer, string]) => {
      const isAdd = url === '/customer/add';
      const title = this.locale.translate(`app.customer.${isAdd ? 'add' : 'edit'}-customer`);
      const name =
        !!customer && Object.hasOwnProperty.call(customer, 'name') ? ` ${customer.name}` : '';
      return `${title} ${isAdd ? '' : name}`;
    }),
  );

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => Number.parseInt(params.get('customerId') ?? '', 10)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((customerId) => {
        if (customerId) {
          this.store.dispatch(setSelectedCustomerId({ customerId }));
        } else {
          this.store.dispatch(clearSelectedCustomer());
        }
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearSelectedCustomer());
  }

  back = () => this.router.navigate(['/customer']);
}
